import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Artists = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/artist`);
      setArtists(response.data);
    } catch (error) {
      console.error('Error fetching artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArtists = artists.filter(artist =>
    artist.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-display font-bold text-gray-900 mb-4 african-accent inline-block">
            Our Artists
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Meet the talented artists bringing African creativity to life
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for artists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-14 rounded-full border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200 text-lg"
            />
            <svg className="absolute left-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Artists Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-square rounded-2xl mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredArtists.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredArtists.map((artist) => (
              <Link
                key={artist._id}
                to={`/artist/${artist._id}`}
                className="group"
              >
                <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-african-lg transition-all duration-300 transform hover:-translate-y-2">
                  {/* Artist Avatar */}
                  <div className="relative bg-gradient-to-br from-orange-400 to-red-500 p-8 flex items-center justify-center aspect-square">
                    <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-6xl font-bold group-hover:scale-110 transition-transform duration-300">
                      {artist.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="absolute top-4 right-4 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>

                  {/* Artist Info */}
                  <div className="p-6">
                    <h3 className="text-2xl font-display font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-200">
                      {artist.name}
                    </h3>
                    <p className="text-orange-600 font-semibold mb-4">
                      🎨 Professional Artist
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between pt-4 border-t border-orange-100">
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900">0</p>
                        <p className="text-xs text-gray-500">Artworks</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900">0</p>
                        <p className="text-xs text-gray-500">Sold</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-900">0</p>
                        <p className="text-xs text-gray-500">Reviews</p>
                      </div>
                    </div>

                    {/* View Profile Button */}
                    <button className="w-full mt-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-xl hover:shadow-lg transform group-hover:scale-105 transition-all duration-300">
                      View Profile
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-orange-200 to-red-200 rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
              No Artists Found
            </h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search' : 'No artists available at the moment'}
            </p>
          </div>
        )}

        {/* Join as Artist CTA */}
        <div className="mt-20 bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 rounded-3xl shadow-african-lg p-12 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-4">
            Are You an Artist?
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Join our community and showcase your artwork to thousands of art lovers around the world
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
          >
            Join as Artist
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Artists;